import { COMMANDS } from '../../../utils/commonProperties';
import * as THREE from 'three';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    setCommandMessage,
    resetOperation,
    setCommandType,
    setOperationData,
} from '../../../redux/operationSlice';
import { clearSelection } from '../../../redux/selectionSlice';
import { useComponentWiring } from '../../../modules/wiring';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import useSnapPoints from '../../../shared/snap/useSnapPoints';
import { toggleSnapMode, toggleOrthoMode } from '../../../redux/modsSlice';
import { MainPanelCommand } from '../../../modules/undoRedo';
import parseColor from '../../../utils/parseColor';
import { ComponentFactory } from '../../../factories/ComponentFactory';
import { FeederFactory } from '../../../factories/FeederFactory';
import { addWire } from '../../../redux/wireSlice';
import { updateSimpleSupplyMetadata } from '../../../redux/simpleSupplySlice';
import { createMainPanel } from '../../../factory/symbols/createMainPanel';
import { store as appStore } from '../../../redux/store';

/**
 * Simple SupplyPoint Main Panel Hook
 * Handles main panel (ADP) drawing with feeder line preview for Simple SupplyPoint architecture
 */
export const useMainPanel = (scene, camera, renderer, undoRedoManagerRef) => {
    const dispatch = useDispatch();
    const { commandType } = useSelector((state) => state.operation);
    const { snapPoints } = useSnapPoints(scene);
    const settings = useSelector((state) => state.settings);
    const dynamicSnapPoints = useRef([]);
    const wiringActiveRef = useRef(false);
    const sceneRef = useRef(null);
    // simpleSupply: useSelector kaldırıldı — event handler'larda lazy read
    const operation = useSelector((state) => state.operation);

    const snapMode = useSelector((state) => state.mods.snapMode);
    const orthoMode = useSelector((state) => state.mods.orthoMode);

    // ✅ Main Panel wire color
    const parsedWireColor = parseColor(settings?.mainPanel?.wireColor ?? 0x2233ff);
    const wireColor = parsedWireColor.colorNumber;

    // ✅ Main Panel Configuration
    const mainPanelConfig = {
        commandType: COMMANDS.DRAW_MAIN_PANEL,
        itemTypeName: 'mainPanel',
        wireColor: wireColor,
        allowSpaceKey: false, // ✅ MainPanel'de Space ile ekleme yok, sadece Enter
        skipItemExecution: true, // ✅ addItem()'da execute atlanır, handleWireEnd'de yapılır
        disablePerpendicularExit: true, // ✅ MainPanel için dik çıkış kuralı devre dışı
        markerCount: 0, // ✅ MainPanel hatlarında wireMarker oluşturma
        createSymbol: (position, direction, angle, isPreview, itemType, extraData = {}) => {
            const colorSetting = isPreview
                ? (settings?.mainPanel?.previewColor ?? settings?.mainPanel?.color ?? wireColor)
                : (settings?.mainPanel?.color ?? wireColor);
            const parsed = parseColor(colorSetting);

            const panelInfo = {
                name: operation.data?.source?.name || 'Ana Pano',
                installedPower: 0,
                phaseCount: operation.data?.source?.phaseCount || 3,
            };

            // ✅ Preview'da da panel position wire direction'ına göre 7.5 birim ötede olmalı
            const normalizedDir = direction.clone().normalize();
            const panelPreviewPos = position.clone().add(normalizedDir.multiplyScalar(7.5));

            const panel = createMainPanel(scene, panelPreviewPos, { isPreview: isPreview }, '', panelInfo);
            panel.children[0].rotation.z = angle; // offsetGroup rotation
            return panel;
        },
        // ❌ createCommand KALDIRILDI - MainPanel için command sadece handleWireEnd içinde oluşturulur
        createCommand: null,
        // ❌ createFeederData null - MainPanel için feeder oluşturma yok (handleWireEnd'de yapılır)
        createFeederData: null,
        // ❌ createComponentData null - MainPanel için component oluşturma yok (handleWireEnd'de yapılır)
        createComponentData: null,
        itemTypes: [
            { name: 'Ana Pano', type: 'mainPanel', power: 0 }
        ],
        onKeyDown: null
    };

    // ✅ Başlangıç: DRAW_MAIN_PANEL aktif olduğunda
    useEffect(() => {
        if (commandType !== COMMANDS.DRAW_MAIN_PANEL || !scene || !camera || !renderer) return;

        dispatch(setCommandMessage('Ana pano bağlamak için başlangıç noktasını seçin'));
        if (!snapMode) dispatch(toggleSnapMode());
        if (!orthoMode) dispatch(toggleOrthoMode());

        sceneRef.current = scene;
        dynamicSnapPoints.current = [];

        // 🔵 SnapPoint oluştur - simpleSupply componentlerinden
        appStore.getState().simpleSupply.components.forEach((component) => {
            if ((component.kind === 'supply' || component.kind === 'meter_panel') && component.position) {
                dynamicSnapPoints.current.push({
                    componentId: component.id,
                    position: new THREE.Vector3(component.position.x, component.position.y, component.position.z),
                    type: 'simpleSupplyConnection',
                    component: component,
                });
            }
        });

        // 🔵 Sahnedeki bağlantı noktaları (panel output points)
        scene.traverse((obj) => {
            if (obj.userData?.connectionPoints) {
                obj.userData.connectionPoints.forEach((p) => {
                    if (p.type === 'panelCon_out') {
                        const worldPos = p.position.clone().applyMatrix4(obj.matrixWorld);
                        dynamicSnapPoints.current.push({
                            no: obj.uuid,
                            position: worldPos,
                            type: 'panelConnection',
                        });
                    }
                });
            }
        });

        wiringActiveRef.current = true;

        // ✅ Cleanup: commandType değiştiğinde wiring'i devre dışı bırak
        return () => {
            wiringActiveRef.current = false;
        };
    }, [commandType, scene, camera, renderer]);

    // ✅ Main Panel placement confirmation
    const confirmPlacement = (vertices, items, source, panel, tempLineUUID, wireMarkers) => {
        if (!vertices || vertices.length < 2) {
            return;
        }

        // ✅ ID'ler
        const mainPanelId = generateUniqueId('mainPanel');
        const feederId = generateUniqueId('mainFeeder');
        const wireId = generateUniqueId('wireMainFeeder');

        // ✅ Main Panel name (source'dan gelen isim)
        const panelName = operation.data?.source?.name || 'Ana Pano';
        const phaseCount = operation.data?.source?.phaseCount || 3;
        const purposeOfUse = operation.data?.source?.purposeOfUse || 'Mesken';
        const tur = operation.data?.source?.tur || 'Tek Sayaç';

        // ADP türünü proje metadata'sına kaydet
        dispatch(updateSimpleSupplyMetadata({ tur }));

        // ✅ Main Panel direction ve position hesaplama (wire panelin KENARINDA bitmeli)
        const lastVertex = vertices[vertices.length - 1];
        const prevVertex = vertices[vertices.length - 2] || vertices[vertices.length - 1];
        const direction = new THREE.Vector3().subVectors(lastVertex, prevVertex).normalize();

        // ✅ Son kenarı EXACTLY 15 birim yap
        const newLastVertex = prevVertex.clone().add(direction.clone().multiplyScalar(15));
        vertices[vertices.length - 1] = newLastVertex;

        const panelAngle = Math.atan2(direction.y, direction.x);

        // Wire panelin kenarında bitmeli, panel merkezi wire sonundan 7.5 birim ötede olmalı
        const panelPosition = newLastVertex.clone().add(direction.clone().multiplyScalar(7.5));

        // ✅ Source component ID
        const sourceComponentId = source?.p?.componentId || source?.componentId;

        // ✅ Component (mainPanel) oluştur
        const mainPanelComponent = ComponentFactory.createPanel('meter_panel', sourceComponentId, {
            id: mainPanelId,
            name: panelName,
            position: { x: panelPosition.x, y: panelPosition.y, z: 0 },
            rotation: { x: 0, y: 0, z: panelAngle },
            electrical: {
                phaseCount: phaseCount,
                voltage: phaseCount === 3 ? 380 : 220,
                purposeOfUse: purposeOfUse,
            },
            metadata: {
                fromComponentId: sourceComponentId,
                threejsGroupId: mainPanelId,
            }
        });

        // ✅ Feeder oluştur
        const cableLength = vertices.reduce((sum, v, i, arr) => {
            if (i === 0) return 0;
            const prev = arr[i - 1];
            return sum + Math.hypot(v.x - prev.x, v.y - prev.y);
        }, 0);

        const mainFeeder = FeederFactory.createMainFeeder(
            sourceComponentId,
            mainPanelId,
            {
                id: feederId,
                vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
                cable: {
                    type: 'NYY',
                    length: Math.round(cableLength / 10),
                    route: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
                },
                electrical: {
                    voltage: phaseCount === 3 ? 380 : 220,
                    phaseCount: phaseCount,
                    purposeOfUse: purposeOfUse,
                },
                metadata: {
                    wireId: wireId
                }
            }
        );

        // ✅ MainPanelCommand oluştur
        const mainPanelCommand = new MainPanelCommand(
            'ADD',
            mainPanelComponent,
            mainFeeder,
            {
                scene: scene,
                dispatch: dispatch,
                wireId: wireId,
                color: wireColor,
                sourceComponentId: sourceComponentId, // ✅ Source component ID (connectedWires için)
                restoreVertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
                restoreSource: source ? {
                    type: source?.type,
                    p: source?.p,
                    no: source?.no,
                    componentId: source?.componentId,
                    position: source?.position ? {
                        x: source.position.x,
                        y: source.position.y,
                        z: source.position.z || 0
                    } : null,
                    name: source?.name,
                    phaseCount: source?.phaseCount,
                    purposeOfUse: source?.purposeOfUse
                } : null,
                // 🆕 REDO mode desteği ile güncellenmiş restoreCallback
                restoreCallback: (restoreVertices, restoreSource, isRedoContext = false, sceneData = null) => {
 
                    dispatch(setCommandType(COMMANDS.DRAW_MAIN_PANEL));

                    setTimeout(() => {
                        const operationData = {
                            initialPoint: restoreVertices && restoreVertices[0] ? restoreVertices[0] : null,
                            source: restoreSource || {
                                type: source?.type,
                                p: source?.p,
                                no: source?.no,
                                componentId: source?.componentId,
                                position: source?.position ? {
                                    x: source.position.x,
                                    y: source.position.y,
                                    z: source.position.z || 0
                                } : null,
                                name: source?.name,
                                phaseCount: source?.phaseCount,
                                purposeOfUse: source?.purposeOfUse
                            },
                            _restoreId: Date.now() + Math.random()
                        };

                        if (isRedoContext) {
                            // REDO: Sadece ilk vertex, geri kalanlar vertexHistory'de
                            operationData.restoreVertices = [restoreVertices[0]];
                            operationData.vertexHistory = restoreVertices.slice(1);
                            operationData.isRedo = true;
                            // 🆕 onRedoComplete callback'ini aktar (chain advancement için)
                            operationData.onRedoComplete = sceneData?.onRedoComplete;
                         } else {
                            // UNDO: Tüm vertices yükle
                            operationData.restoreVertices = restoreVertices;
                            operationData.isRedo = false;
                         }

                        dispatch(setOperationData(operationData));
                    }, 16);
                }
            }
        );

        // ✅ Global Undo/Redo transaction
        if (undoRedoManagerRef?.current) {
            const tx = undoRedoManagerRef.current.beginTransaction('Add Main Panel');
            undoRedoManagerRef.current.addCommand(mainPanelCommand);
            undoRedoManagerRef.current.commitTransaction(tx);
        }

        mainPanelCommand.execute();

        // ✅ Wire'ı wireSlice'a ekle
        dispatch(addWire({
            id: wireId,
            type: 'polyline',
            vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
            layer: 'simpleMainPanel',
            color: '#' + wireColor.toString(16).padStart(6, '0'),
            userData: {
                selectable: true,
                parent: 'simpleMainPanel',
                componentId: mainPanelId
            }
        }));

        dispatch(setCommandMessage('Ana pano yerleştirildi (Ctrl+Z ile geri alınabilir)'));
        dispatch(resetOperation());
        dispatch(clearSelection()); // ✅ ESC ile aynı temizlik
        dispatch(setCommandMessage('')); // ✅ Mesajı temizle
        wiringActiveRef.current = false;

        return { wireId: wireId, mainPanelId: mainPanelId };
    };

    // ✅ Main Panel wire end handler
    const handleWireEnd = (vertices, items, source, panel, tempLineUUID, wireMarkers) => {
        return confirmPlacement(vertices, items, source, panel, tempLineUUID, wireMarkers);
    };

    // ✅ useComponentWiring entegrasyonu
    useComponentWiring(
        scene,
        camera,
        renderer,
        [...snapPoints, ...dynamicSnapPoints.current],
        {
            ...mainPanelConfig,
            enabled: () => wiringActiveRef.current,
            onWireEnd: handleWireEnd,
            onItemAdd: (item) => {
             },
            onPreviewUpdate: () => {},
            onWireCancel: () => {
                wiringActiveRef.current = false;
                dispatch(resetOperation());
                dispatch(clearSelection());
                dispatch(setCommandMessage(''));
            },
            undoRedoManagerRef: undoRedoManagerRef // ✅ Auto-finish için gerekli
        }
    );
};

/**
 * Trigger function to start main panel drawing from a source component
 * This function is called by BoxTypeMenu when ADP button is clicked
 */
export const startSimpleMainPanelDrawing = (
    dispatch,
    sourceComponentId,
    sourcePosition,
    details
) => {
    dispatch(setCommandType(COMMANDS.DRAW_MAIN_PANEL));
    dispatch(
        setOperationData({
            initialPoint: sourcePosition,
            source: {
                type: 'simpleSupplyConnection',
                componentId: sourceComponentId,
                position: sourcePosition,
                name: details.panoAdi,
                phaseCount: details.phaseCount || 3,
                purposeOfUse: details.kullanimAmac || 'Mesken',
                tur: details.tur || 'Tek Sayaç',
            },
        })
    );
};

export default useMainPanel;
